import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { Plus, Trash2, AlertCircle } from "lucide-react";
import type { VisibilityRules, VisibilityCondition, FieldType } from "../types";

// Tipo mínimo aceito — compatível com MissionField e FormBuilderField
export interface FieldLike {
  key: string;
  label: string;
  field_type: string;
  options?: any[] | null;
}

interface VisibilityRulesBuilderProps {
  rules: VisibilityRules | null;
  allFields: FieldLike[];
  currentFieldKey: string;
  onChange: (rules: VisibilityRules | null) => void;
}

export const VisibilityRulesBuilder = ({
  rules,
  allFields,
  currentFieldKey,
  onChange,
}: VisibilityRulesBuilderProps) => {
  const inputBg = useColorModeValue("white", "gray.800");
  const inputBorder = useColorModeValue("gray.200", "gray.600");
  const labelColor = useColorModeValue("gray.700", "gray.300");
  const subColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const warningBg = useColorModeValue("rgba(168,85,247,0.08)", "rgba(168,85,247,0.1)");
  const warningBorder = useColorModeValue("rgba(168,85,247,0.3)", "rgba(168,85,247,0.2)");
  const warningText = useColorModeValue("purple.700", "purple.300");

  const availableFields = allFields.filter((f) => f.key !== currentFieldKey);

  const handleAddCondition = () => {
    const newRules = rules || { conditions: [], logic: "AND" as const };
    if (availableFields.length === 0) return;

    newRules.conditions.push({
      field_key: availableFields[0].key,
      operator: "equals",
      value: "",
    });
    onChange({ ...newRules });
  };

  const handleRemoveCondition = (index: number) => {
    if (!rules) return;
    const newRules = { ...rules };
    newRules.conditions = newRules.conditions.filter((_, i) => i !== index);

    if (newRules.conditions.length === 0) {
      onChange(null);
    } else {
      onChange(newRules);
    }
  };

  const handleUpdateCondition = (index: number, condition: VisibilityCondition) => {
    if (!rules) return;
    const newRules = { ...rules };
    newRules.conditions[index] = condition;
    onChange(newRules);
  };

  const handleChangeLogic = (logic: "AND" | "OR") => {
    if (!rules) return;
    onChange({ ...rules, logic });
  };

  const handleClear = () => {
    onChange(null);
  };

  if (availableFields.length === 0) {
    return (
      <Box
        p={4}
        borderRadius="md"
        bg={warningBg}
        border="1px solid"
        borderColor={warningBorder}
      >
        <Flex align="flex-start" gap={2}>
          <Icon as={AlertCircle} color={warningText} flexShrink={0} mt={0.5} boxSize={4} />
          <Text fontSize="sm" color={warningText}>
            Nenhum outro campo disponível para criar regras de visibilidade.
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      {rules && rules.conditions.length > 0 ? (
        <>
          <Box>
            <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={3}>
              Condições
            </FormLabel>
            <VStack align="stretch" spacing={3}>
              {rules.conditions.map((condition, idx) => (
                <ConditionRow
                  key={idx}
                  condition={condition}
                  availableFields={availableFields}
                  onUpdate={(updated) => handleUpdateCondition(idx, updated)}
                  onRemove={() => handleRemoveCondition(idx)}
                  inputBg={inputBg}
                  inputBorder={inputBorder}
                />
              ))}
            </VStack>
          </Box>

          {rules.conditions.length > 1 && (
            <Box>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={2}>
                Lógica
              </FormLabel>
              <HStack spacing={3}>
                <Button
                  size="sm"
                  variant={rules.logic === "AND" ? "solid" : "outline"}
                  colorScheme={rules.logic === "AND" ? "blue" : "gray"}
                  onClick={() => handleChangeLogic("AND")}
                  flex={1}
                >
                  E (AND)
                </Button>
                <Button
                  size="sm"
                  variant={rules.logic === "OR" ? "solid" : "outline"}
                  colorScheme={rules.logic === "OR" ? "blue" : "gray"}
                  onClick={() => handleChangeLogic("OR")}
                  flex={1}
                >
                  Ou (OR)
                </Button>
              </HStack>
              <Text fontSize="xs" color={subColor} mt={2}>
                {rules.logic === "AND"
                  ? "Todas as condições devem ser atendidas"
                  : "Qualquer uma das condições deve ser atendida"}
              </Text>
            </Box>
          )}

          <HStack spacing={2}>
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              w="full"
              onClick={handleClear}
            >
              Limpar regras
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              w="full"
              onClick={handleAddCondition}
              isDisabled={availableFields.length === 0}
            >
              + Adicionar condição
            </Button>
          </HStack>
        </>
      ) : (
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          w="full"
          leftIcon={<Plus size={16} />}
          onClick={handleAddCondition}
          isDisabled={availableFields.length === 0}
        >
          Adicionar regra de visibilidade
        </Button>
      )}
    </VStack>
  );
};

interface ConditionRowProps {
  condition: VisibilityCondition;
  availableFields: FieldLike[];
  onUpdate: (condition: VisibilityCondition) => void;
  onRemove: () => void;
  inputBg: string;
  inputBorder: string;
}

const ConditionRow = ({
  condition,
  availableFields,
  onUpdate,
  onRemove,
  inputBg,
  inputBorder,
}: ConditionRowProps) => {
  const selectedField = availableFields.find((f) => f.key === condition.field_key);
  const fieldType = selectedField?.field_type;

  // Campos que são booleanos (sim/não)
  const booleanTypes: FieldType[] = ["boolean", "pcd"];
  const isBooleanField = booleanTypes.includes(fieldType as FieldType);

  // Campos que são select/multi_select
  const selectTypes: FieldType[] = ["select", "multi_select"];
  const isSelectField = selectTypes.includes(fieldType as FieldType);

  // Operadores que não precisam de valor (só olham se o campo está vazio).
  const isEmptyOperator =
    condition.operator === "is_empty" || condition.operator === "is_not_empty";

  // Obter opções do campo selecionado
  const fieldOptions = selectedField?.options || [];

  const handleFieldChange = (newFieldKey: string) => {
    const newField = availableFields.find((f) => f.key === newFieldKey);
    const newFieldType = newField?.field_type;

    // Reset operator e value quando muda o campo
    let newOperator: VisibilityCondition["operator"] = "equals";
    let newValue: string | string[] = "";

    // Para campos booleanos, usar "equals" como padrão
    if (booleanTypes.includes(newFieldType as FieldType)) {
      newOperator = "equals";
      newValue = "true";
    }
    // Para campos select, usar "in" como padrão
    else if (selectTypes.includes(newFieldType as FieldType)) {
      newOperator = "in";
      newValue = [];
    }

    onUpdate({
      field_key: newFieldKey,
      operator: newOperator,
      value: newValue,
    });
  };

  const handleOperatorChange = (newOperator: VisibilityCondition["operator"]) => {
    // Operadores is_empty/is_not_empty não usam value — limpa pra não
    // deixar um valor órfão salvo no JSON.
    const newValue =
      newOperator === "is_empty" || newOperator === "is_not_empty"
        ? ""
        : condition.value;
    onUpdate({ ...condition, operator: newOperator, value: newValue });
  };

  return (
    <VStack align="stretch" spacing={2}>
      <HStack align="flex-start" spacing={2}>
        <Select
          size="sm"
          value={condition.field_key}
          onChange={(e) => handleFieldChange(e.target.value)}
          bg={inputBg}
          borderColor={inputBorder}
          flex={1}
        >
          {availableFields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </Select>

        {!isSelectField && !isBooleanField && (
          <Select
            size="sm"
            value={condition.operator}
            onChange={(e) => handleOperatorChange(e.target.value as VisibilityCondition["operator"])}
            bg={inputBg}
            borderColor={inputBorder}
            w="140px"
          >
            <option value="equals">é igual a</option>
            <option value="not_equals">não é igual a</option>
            <option value="contains">contém</option>
            <option value="in">está em</option>
            <option value="not_in">não está em</option>
            <option value="is_empty">está vazio</option>
            <option value="is_not_empty">não está vazio</option>
          </Select>
        )}

        {(isSelectField || isBooleanField) && (
          <Select
            size="sm"
            value={condition.operator}
            onChange={(e) => handleOperatorChange(e.target.value as VisibilityCondition["operator"])}
            bg={inputBg}
            borderColor={inputBorder}
            w="120px"
          >
            <option value="equals">é igual a</option>
            <option value="not_equals">não é igual a</option>
            {isSelectField && <option value="in">está em</option>}
            {isSelectField && <option value="not_in">não está em</option>}
            <option value="is_empty">está vazio</option>
            <option value="is_not_empty">não está vazio</option>
          </Select>
        )}

        <IconButton
          aria-label="Remover condição"
          icon={<Trash2 size={16} />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={onRemove}
          flexShrink={0}
        />
      </HStack>

      {/* Valor baseado no tipo de campo — oculto para operadores is_empty/is_not_empty */}
      {!isEmptyOperator && (
      <Box pl={2}>
        {isBooleanField ? (
          // Campos booleanos: Sim/Não
          <RadioGroup
            value={typeof condition.value === "string" ? condition.value : "true"}
            onChange={(val) => onUpdate({ ...condition, value: val })}
          >
            <Stack direction="row" spacing={6}>
              <Radio value="true" size="sm">
                <Text fontSize="sm">Sim</Text>
              </Radio>
              <Radio value="false" size="sm">
                <Text fontSize="sm">Não</Text>
              </Radio>
            </Stack>
          </RadioGroup>
        ) : isSelectField ? (
          // Campos select: Checkboxes com opções
          <CheckboxGroup
            value={Array.isArray(condition.value) ? condition.value : [condition.value as string]}
            onChange={(vals) => onUpdate({ ...condition, value: vals })}
          >
            <Stack spacing={2}>
              {fieldOptions.map((opt: any) => (
                <Checkbox key={opt.value} value={opt.value} size="sm">
                  <Text fontSize="sm">{opt.label}</Text>
                </Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
        ) : (
          // Outros campos: Input de texto livre
          <Input
            size="sm"
            placeholder="Digite o valor"
            value={typeof condition.value === "string" ? condition.value : condition.value.join(", ")}
            onChange={(e) => onUpdate({ ...condition, value: e.target.value })}
            bg={inputBg}
            borderColor={inputBorder}
          />
        )}
      </Box>
      )}
    </VStack>
  );
};
